import React, { useContext, useEffect, useCallback } from 'react';
import { en, fa, languages, type Language, type TranslationKeys } from '@/i18n';
import { useStore } from '@/store/useStore';
import { LanguageContext } from './languageContextInstance';

type NestedKeyOf<T> = T extends object
  ? { [K in keyof T]: K extends string
      ? T[K] extends object
        ? `${K}` | `${K}.${NestedKeyOf<T[K]>}`
        : `${K}`
      : never
    }[keyof T]
  : never;

type TranslationKey = NestedKeyOf<TranslationKeys>;

const translations: Record<Language, TranslationKeys> = { en, fa };

function getNestedValue(obj: any, path: string): string {
  const value = path.split('.').reduce((acc, part) => acc?.[part], obj);
  return typeof value === 'string' ? value : path;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { language, setLanguage: setStoreLanguage } = useStore();

  const setLanguage = useCallback((lang: Language) => {
    setStoreLanguage(lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = languages[lang].dir;
    
    // Update font family based on language
    if (lang === 'fa') {
      document.documentElement.classList.add('font-persian');
      document.documentElement.classList.remove('font-english');
    } else {
      document.documentElement.classList.add('font-english');
      document.documentElement.classList.remove('font-persian');
    }
  }, [setStoreLanguage]);

  const t = useCallback((key: TranslationKey, params?: Record<string, string | number>): string => {
    let value = getNestedValue(translations[language], key);
    
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        value = value.replace(`{${paramKey}}`, String(paramValue));
      });
    }
    
    return value;
  }, [language]);

  const dir = languages[language].dir;
  const isRTL = dir === 'rtl';

  // Initialize language settings on mount
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = languages[language].dir;
    
    if (language === 'fa') {
      document.documentElement.classList.add('font-persian');
      document.documentElement.classList.remove('font-english');
    } else {
      document.documentElement.classList.add('font-english');
      document.documentElement.classList.remove('font-persian');
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
